import { useState, useCallback } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import { Project } from '../types/project';

export const useProjects = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadProjects = useCallback(async (attempt = 1) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PROJECTS}`, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                if (res.status === 401) {
                    throw new Error("Authentication required");
                }
                throw new Error("Failed to load projects");
            }

            const data = await res.json();
            setProjects(data);
            setError(null);
        } catch (err) {
            if (attempt < 3) {
                setTimeout(() => {
                    loadProjects(attempt + 1);
                }, 400);
            } else {
                setError(err instanceof Error ? err.message : "Failed to load projects");
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const deleteProject = useCallback(async (projectId: string) => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DELETE_PROJECT}?project_id=${projectId}`, {
                method: 'DELETE',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error("Failed to delete project");
            }

            setProjects(prev => prev.filter(p => p.project_id !== projectId));
            return true;
        } catch (err) {
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const setProjectTags = useCallback(async (projectId: string, tags: string[]) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SET_PROJECT_TAGS}`, {
                method: 'POST',
                headers: getAuthHeadersWithoutCredentials(),
                body: JSON.stringify({
                    project_id: projectId,
                    tags: tags
                })
            });

            if (!res.ok) {
                throw new Error("Failed to set project tags");
            }

            return await res.json();
        } catch (err) {
            throw err;
        }
    }, []);

    return {
        projects,
        isLoading,
        error,
        loadProjects,
        deleteProject,
        setProjectTags,
    };
};